import Intercom, {
  IntercomEvents,
  Visibility,
} from '@intercom/intercom-react-native';
import { NativeEventEmitter, NativeModules, Platform } from 'react-native';
import Logger from './Logger';

let isInitialized = false;

/**
 * Set callback for unread count changes
 */

let onUnreadCountChange: ((count: number) => void) | null = null;

export const setUnreadCountChangeCallback = (
  callback: ((count: number) => void) | null
) => {
  onUnreadCountChange = callback;
};

export const initializeIntercom = async (
  appId: string,
  iosApiKey: string,
  androidApiKey: string
) => {
  try {
    const apiKey = Platform.select({ ios: iosApiKey, android: androidApiKey });
    if (!apiKey || !appId) {
      Logger.warn('Intercom initialization skipped: missing apiKey or appId');
      return;
    }
    await Intercom.initialize(apiKey, appId);
    // Hide both the launcher AND in-app messages to prevent bottom navigation overlap
    await Intercom.setLauncherVisibility(Visibility.GONE);
    await Intercom.setInAppMessageVisibility(Visibility.GONE);
    isInitialized = true;
  } catch (e) {
    Logger.error(`Intercom initialization failed - ${e}`);
  }
};

export const loginIntercomUnidentifiedUser = async () => {
  if (!isInitialized) {
    return;
  }

  try {
    await Intercom.loginUnidentifiedUser();
  } catch {
    // loginUnidentifiedUser fails when a stale identified session exists in the SDK.
    // Logout first to clear it, then retry.
    try {
      await Intercom.logout();
      await Intercom.loginUnidentifiedUser();
    } catch (e) {
      Logger.warn(`Intercom loginUnidentifiedUser failed - ${e}`);
    }
  }
};

export const loginIntercomUser = async (userId: string, email?: string) => {
  try {
    await Intercom.loginUserWithUserAttributes({ userId, email });
  } catch (e) {
    Logger.error(`Intercom login failed - ${e}`);
  }
};

export const logoutIntercomUser = async () => {
  try {
    await Intercom.logout();
  } catch (e) {
    Logger.error(`Intercom logout failed - ${e}`);
  }
};

export const sendTokenToIntercom = async (token: string) => {
  // Don't send token if Intercom is not initialized
  if (!isInitialized) {
    Logger.debug('Intercom: skipping token send - not initialized yet');
    return;
  }
  try {
    await Intercom.sendTokenToIntercom(token);
  } catch (e) {
    Logger.warn(`Intercom sendToken failed - ${e}`);
  }
};

export const isIntercomReady = (): boolean => isInitialized;

export const presentIntercom = async () => {
  if (!isInitialized) {
    return;
  }
  try {
    await Intercom.present();
  } catch (e) {
    Logger.error(`Intercom present failed - ${e}`);
  }
};

/**
 * Sets up event listener for unread count changes.
 * Returns a cleanup function to remove the listener.
 * Uses Intercom's event emitter via NativeModules.
 */
export const setupIntercomEventListeners = (): (() => void) => {
  // Bootstrap event listeners for Android (required for Android native events)
  let cleanupAndroid: (() => void) | undefined;

  // MUST check platform or iOS crashes immediately
  if (Platform.OS === 'android') {
    cleanupAndroid = Intercom.bootstrapEventListeners();
  }

  // Create event emitter from Intercom's native module
  // TypeScript doesn't know about this internal module, so we've to use type assertion
  const emitter = new NativeEventEmitter(
    NativeModules.IntercomEventEmitter as any
  );

  // Listen for unread count changes and notify subscribers
  const unreadListener = emitter.addListener(
    IntercomEvents.IntercomUnreadCountDidChange,
    (response: { count: number }) => {
      onUnreadCountChange?.(response.count);
    }
  );

  // Return cleanup function
  return () => {
    unreadListener.remove();
    cleanupAndroid?.();
  };
};
